import { useEffect, useRef } from 'react';

/**
 * 卸载时把"仅存在于本地 state、尚未提交到 store"的 instructions 刷写回节点数据。
 *
 * 背景：image/video 节点的 prompt 输入采用"本地 state + 失焦才提交"。当关闭
 * "渲染全部节点"（React Flow 的 onlyRenderVisibleElements）后，节点移出视口会被卸载，
 * 而卸载不会触发 textarea 的 blur，未提交的本地值就会丢失；回到视口重新挂载时读到的
 * 还是旧的 data.instructions。这里用 ref 跟踪最新值，在 unmount 时补一次提交。
 *
 * 用 ref 镜像所有入参 + 空依赖 effect，确保 cleanup **只在真正卸载时**执行一次：
 * 若依赖 commit/committed 的引用，它们变化时 effect 会重跑，cleanup 会在编辑中途误提交。
 */
export function useFlushInstructionsOnUnmount(
  localInstructions: string,
  committedInstructions: string,
  commit: (text: string) => void,
) {
  const localRef = useRef(localInstructions);
  localRef.current = localInstructions;
  const committedRef = useRef(committedInstructions);
  committedRef.current = committedInstructions;
  const commitRef = useRef(commit);
  commitRef.current = commit;

  useEffect(
    () => () => {
      const local = localRef.current;
      if (local !== (committedRef.current ?? '')) {
        try {
          commitRef.current(local);
        } catch {}
      }
    },
    [],
  );
}
